import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import './BrokerageChecklist.css';
import { getListingsApiUrl } from '../utils/apiUrls';

const LISTINGS_URL = getListingsApiUrl();


const ListingChecklist = ({ listingId, syncStatus, isExpanded, onToggle }) => {
  const { user } = useAuth0();
  const [checklist, setChecklist] = useState(null);

  useEffect(() => {
    if (listingId && user?.email) fetchChecklist();
  }, [listingId, user?.email]);

  const fetchChecklist = async () => {
    try {
      const res = await axios.get(
        `${LISTINGS_URL}/${listingId}/checklist?user_email=${encodeURIComponent(user.email)}`
      );
      setChecklist(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setChecklist({ tms_configured: false });
      } else {
        console.error('Error fetching listing checklist:', err);
      }
    }
  };

  // Don't render if TMS not configured or listing not synced to SkySlope
  if (checklist && checklist.tms_configured === false) return null;
  if (!checklist) return null;

  const needed = checklist.needed || [];
  const onFile = checklist.on_file || [];

  const requiredNeeded = needed.filter(item => item.is_required);
  const optionalNeeded = needed.filter(item => !item.is_required);
  const requiredOnFile = onFile.filter(item => item.is_required);
  const requiredTotal = requiredNeeded.length + requiredOnFile.length;

  const statusLabel = (() => {
    const s = syncStatus || checklist.sync_status;
    if (s === 'synced') return { text: '● Synced', cls: 'tms-status-synced' };
    if (s === 'error') return { text: '● Sync Error', cls: 'tms-status-error' };
    if (s === 'pending') return { text: '◌ Pending', cls: 'tms-status-pending' };
    return null;
  })();

  return (
    <div className="brokerage-checklist-section">
      <div className="bc-header" onClick={onToggle}>
        <div className="bc-header-left">
          <h2 className="bc-header-title">Brokerage Document Checklist</h2>
          {statusLabel && (
            <span className={`tms-sync-status ${statusLabel.cls}`}>{statusLabel.text}</span>
          )}
        </div>
        <div className="bc-header-right">
          {requiredTotal > 0 && (
            <div className="bc-progress">
              <div className="bc-progress-bar">
                <div
                  className="bc-progress-fill"
                  style={{ width: `${Math.round((requiredOnFile.length / requiredTotal) * 100)}%` }}
                />
              </div>
              <span className="bc-progress-label">
                {requiredOnFile.length}/{requiredTotal}
              </span>
            </div>
          )}
          <button
            className="bc-chevron"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg
              width="20" height="20" viewBox="0 0 24 24"
              fill="none" stroke="currentColor"
              strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="bc-body">
          {requiredNeeded.length > 0 && (
            <ChecklistGroup
              label="Required"
              count={requiredNeeded.length}
              items={requiredNeeded}
              variant="needed"
              icon="○"
            />
          )}
          {optionalNeeded.length > 0 && (
            <ChecklistGroup
              label="Optional"
              count={optionalNeeded.length}
              items={optionalNeeded}
              variant="optional"
              icon="○"
            />
          )}
          {needed.length === 0 && (
            <p className="bc-all-clear">All required documents are on file.</p>
          )}
          <ChecklistGroup
            label="On File"
            count={onFile.length}
            items={onFile}
            variant="on-file"
            icon="✓"
          />
        </div>
      )}
    </div>
  );
};

const ChecklistGroup = ({ label, count, items, variant, icon }) => (
  <div className={`bc-group bc-group--${variant}`}>
    <div className="bc-group-header">
      <span className={`bc-group-label bc-group-label--${variant}`}>{label}</span>
      <span className="bc-group-count">{count} {count === 1 ? 'doc' : 'docs'}</span>
    </div>
    {items.length === 0 ? (
      <p className="bc-group-empty">None yet</p>
    ) : (
      <div className="bc-items">
        {items.map((item, i) => (
          <div key={i} className={`bc-item bc-item--${variant}`}>
            <span className="bc-item-icon">{icon}</span>
            <span className="bc-item-name">{item.item_name}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default ListingChecklist;
